import { useState, type Dispatch, type DragEvent, type SetStateAction } from "react"
import { toast } from "sonner"

import type { ReportTemplateFieldDto, ReportTemplateFilterDto } from "../../api/reportTemplateService"
import {
  fieldFormInitial,
  filterFormInitial,
  normalizeOrder,
  reindexFields,
  reindexFilters,
  type FieldFormState,
  type FilterFormState,
} from "./editorUtils"

type UseReportTemplateFieldFilterConfigParams = {
  fields: ReportTemplateFieldDto[]
  setFields: Dispatch<SetStateAction<ReportTemplateFieldDto[]>>
  filters: ReportTemplateFilterDto[]
  setFilters: Dispatch<SetStateAction<ReportTemplateFilterDto[]>>
  fieldKeyOptions: string[]
}

export function useReportTemplateFieldFilterConfig({
  fields,
  setFields,
  filters,
  setFilters,
  fieldKeyOptions,
}: UseReportTemplateFieldFilterConfigParams) {
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false)
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null)
  const [fieldForm, setFieldForm] = useState<FieldFormState>(fieldFormInitial)
  const [dragFieldIndex, setDragFieldIndex] = useState<number | null>(null)
  const [dragOverFieldIndex, setDragOverFieldIndex] = useState<number | null>(null)

  const [filterDialogOpen, setFilterDialogOpen] = useState(false)
  const [editingFilterIndex, setEditingFilterIndex] = useState<number | null>(null)
  const [filterForm, setFilterForm] = useState<FilterFormState>(filterFormInitial)
  const [dragFilterIndex, setDragFilterIndex] = useState<number | null>(null)
  const [dragOverFilterIndex, setDragOverFilterIndex] = useState<number | null>(null)

  const openAddField = () => {
    setEditingFieldIndex(null)
    setFieldForm({
      ...fieldFormInitial,
      orderIndex: fields.length + 1,
    })
    setFieldDialogOpen(true)
  }

  const openEditField = (index: number) => {
    const item = fields[index]
    if (!item) return
    setEditingFieldIndex(index)
    setFieldForm({
      fieldKey: item.fieldKey,
      label: item.label,
      dataType: item.dataType,
      sourcePath: item.sourcePath ?? "",
      isFilterable: item.isFilterable,
      isSortable: item.isSortable,
      isGroupable: item.isGroupable,
      defaultVisible: item.defaultVisible,
      orderIndex: item.orderIndex,
    })
    setFieldDialogOpen(true)
  }

  const saveField = () => {
    if (!fieldForm.fieldKey.trim()) {
      toast.error("Field Key is required")
      return
    }
    if (!fieldForm.label.trim()) {
      toast.error("Field Label is required")
      return
    }

    const payload: ReportTemplateFieldDto = {
      fieldKey: fieldForm.fieldKey.trim(),
      label: fieldForm.label.trim(),
      dataType: fieldForm.dataType,
      sourcePath: fieldForm.sourcePath.trim() || null,
      isFilterable: fieldForm.isFilterable,
      isSortable: fieldForm.isSortable,
      isGroupable: fieldForm.isGroupable,
      defaultVisible: fieldForm.defaultVisible,
      orderIndex: normalizeOrder(fieldForm.orderIndex, fields.length + 1),
    }

    setFields((prev) => {
      if (editingFieldIndex === null) {
        return reindexFields([...prev, payload].sort((a, b) => a.orderIndex - b.orderIndex))
      }
      return reindexFields(
        prev
          .map((item, index) => (index === editingFieldIndex ? { ...item, ...payload } : item))
          .sort((a, b) => a.orderIndex - b.orderIndex),
      )
    })
    setFieldDialogOpen(false)
  }

  const deleteField = (index: number) => {
    setFields((prev) => reindexFields(prev.filter((_, currentIndex) => currentIndex !== index)))
  }

  const handleFieldDragStart = (index: number) => {
    setDragFieldIndex(index)
    setDragOverFieldIndex(index)
  }

  const handleFieldDragOver = (index: number, event: DragEvent<HTMLTableRowElement>) => {
    event.preventDefault()
    if (dragOverFieldIndex !== index) {
      setDragOverFieldIndex(index)
    }
  }

  const handleFieldDrop = (dropIndex: number) => {
    if (dragFieldIndex === null || dragFieldIndex === dropIndex) {
      setDragFieldIndex(null)
      setDragOverFieldIndex(null)
      return
    }

    setFields((prev) => {
      const next = [...prev]
      const [moved] = next.splice(dragFieldIndex, 1)
      if (!moved) return prev
      next.splice(dropIndex, 0, moved)
      return reindexFields(next)
    })
    setDragFieldIndex(null)
    setDragOverFieldIndex(null)
  }

  const handleFieldDragEnd = () => {
    setDragFieldIndex(null)
    setDragOverFieldIndex(null)
  }

  const openAddFilter = () => {
    setEditingFilterIndex(null)
    setFilterForm({
      ...filterFormInitial,
      fieldKey: fieldKeyOptions[0] ?? "",
      orderIndex: filters.length + 1,
    })
    setFilterDialogOpen(true)
  }

  const openEditFilter = (index: number) => {
    const item = filters[index]
    if (!item) return
    setEditingFilterIndex(index)
    setFilterForm({
      fieldKey: item.fieldKey,
      operator: item.operator,
      defaultValueJson: item.defaultValueJson ?? "",
      required: item.required,
      orderIndex: item.orderIndex,
    })
    setFilterDialogOpen(true)
  }

  const saveFilter = () => {
    if (!filterForm.fieldKey.trim()) {
      toast.error("Filter field is required")
      return
    }
    if (!filterForm.operator.trim()) {
      toast.error("Operator is required")
      return
    }

    const payload: ReportTemplateFilterDto = {
      fieldKey: filterForm.fieldKey.trim(),
      operator: filterForm.operator.trim(),
      defaultValueJson: filterForm.defaultValueJson.trim() || null,
      required: filterForm.required,
      orderIndex: normalizeOrder(filterForm.orderIndex, filters.length + 1),
    }

    setFilters((prev) => {
      if (editingFilterIndex === null) {
        return reindexFilters([...prev, payload].sort((a, b) => a.orderIndex - b.orderIndex))
      }
      return reindexFilters(
        prev
          .map((item, index) => (index === editingFilterIndex ? { ...item, ...payload } : item))
          .sort((a, b) => a.orderIndex - b.orderIndex),
      )
    })
    setFilterDialogOpen(false)
  }

  const deleteFilter = (index: number) => {
    setFilters((prev) => reindexFilters(prev.filter((_, currentIndex) => currentIndex !== index)))
  }

  const handleFilterDragStart = (index: number) => {
    setDragFilterIndex(index)
    setDragOverFilterIndex(index)
  }

  const handleFilterDragOver = (index: number, event: DragEvent<HTMLTableRowElement>) => {
    event.preventDefault()
    if (dragOverFilterIndex !== index) {
      setDragOverFilterIndex(index)
    }
  }

  const handleFilterDrop = (dropIndex: number) => {
    if (dragFilterIndex === null || dragFilterIndex === dropIndex) {
      setDragFilterIndex(null)
      setDragOverFilterIndex(null)
      return
    }

    setFilters((prev) => {
      const next = [...prev]
      const [moved] = next.splice(dragFilterIndex, 1)
      if (!moved) return prev
      next.splice(dropIndex, 0, moved)
      return reindexFilters(next)
    })

    setDragFilterIndex(null)
    setDragOverFilterIndex(null)
  }

  const handleFilterDragEnd = () => {
    setDragFilterIndex(null)
    setDragOverFilterIndex(null)
  }

  return {
    fieldDialogOpen,
    setFieldDialogOpen,
    editingFieldIndex,
    fieldForm,
    setFieldForm,
    dragFieldIndex,
    dragOverFieldIndex,
    openAddField,
    openEditField,
    saveField,
    deleteField,
    handleFieldDragStart,
    handleFieldDragOver,
    handleFieldDrop,
    handleFieldDragEnd,
    filterDialogOpen,
    setFilterDialogOpen,
    editingFilterIndex,
    filterForm,
    setFilterForm,
    dragFilterIndex,
    dragOverFilterIndex,
    openAddFilter,
    openEditFilter,
    saveFilter,
    deleteFilter,
    handleFilterDragStart,
    handleFilterDragOver,
    handleFilterDrop,
    handleFilterDragEnd,
  }
}

